import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import FormSpinner from 'components/FormSpinner';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { usePersonNameDialog } from '../../hooks/usePersonNameDialog';
import { getPeopleNames, deletePersonName } from '../../services/personNameService';
import { NAME_GRID_COLUMNS } from '../../constants/personNameFormConstants';

const PersonNamesTab = ({ personId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [names, setNames] = useState([]);
  const { openPersonNameDialog } = usePersonNameDialog();
  const canEditPerson = usePermissions(PERMISSION_RESOURCES.PERSON).Edit;
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadNames = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleNames(personId),
      onSuccess: (data) => setNames(data),
    });
  }, [dispatch, personId]);

  const handleAdd = () => openPersonNameDialog({ personId, onSaved: loadNames });

  const handleEdit = (row) =>
    openPersonNameDialog({ personId, nameId: row.ptsNameID, onSaved: loadNames });

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete "${row.FullName || row.LastName}"?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deletePersonName(row.ptsNameID),
            onSuccess: () => {
              dispatch(notify('Name deleted successfully', 'success'));
              loadNames();
            },
          });
        },
        'Delete Name'
      );
    },
    [dispatch, requestConfirm, loadNames]
  );

  useEffect(() => {
    loadNames();
  }, [loadNames]);

  return (
    <Box className="person-names-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Name"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPerson}
        />
      </Box>

      <CommonDataGrid
        rows={names}
        columns={NAME_GRID_COLUMNS}
        getRowId={(row) => row.ptsNameID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Names' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPerson },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPerson },
        }}
      />

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PersonNamesTab;
